import React, { useState, useMemo, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useWistorAuth } from '../../api/auth'
import {
  useUsers, useProjects,
  useCreateUser, useUpdateUser, useDeleteUser,
} from '../shared/use-v2-queries'
import orderBy from 'lodash/orderBy'

// ─── Types ────────────────────────────────────────────
type UserItem = {
  id: string
  name: string
  email: string
  role: string
  lastLogin?: string | null
  deleted?: boolean | null
  project?: { id: string; title: string; path: string } | null
}

// ─── Icons ────────────────────────────────────────────
const ic = {
  search: 'M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z',
  plus: 'M12 5v14M5 12h14',
  edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  key: 'M21 2l-2 2m-7.61 7.61a5.5 5.5 0 11-7.778 7.778 5.5 5.5 0 017.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4',
  trash: 'M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2',
  x: 'M18 6L6 18M6 6l12 12',
}

function Icon({ d, size = 16 }: { d: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

const ROLE_OPTIONS = ['viewer', 'editor', 'admin', 'superadmin'] as const
const ROLE_BADGE_CLASS: Record<string, string> = {
  superadmin: 'v2-badge--superadmin',
  admin: 'v2-badge--admin',
  editor: 'v2-badge--editor',
  viewer: 'v2-badge--viewer',
}

// ─── Dialog ───────────────────────────────────────────
function V2Dialog({
                    open, onClose, title, children, footer,
                  }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode; footer: React.ReactNode
}) {
  if (!open) return null
  return (
    <div className="v2-dialog-overlay" onClick={onClose}>
      <div className="v2-dialog" onClick={e => e.stopPropagation()}>
        <div className="v2-dialog-header"><div className="v2-dialog-title">{title}</div></div>
        <div className="v2-dialog-body">{children}</div>
        <div className="v2-dialog-footer">{footer}</div>
      </div>
    </div>
  )
}

// ─── useIsMobile ──────────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false
  )
  useEffect(() => {
    const h = () => setMobile(window.innerWidth < breakpoint)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [breakpoint])
  return mobile
}

// ─── Main Export ───────────────────────────────────────
export function UsersV2() {
  const auth = useWistorAuth()
  const history = useHistory()

  useEffect(() => {
    if (auth.status !== 'authorized') return history.push('/login', 'users')
    if (!['admin', 'superadmin'].includes(auth.user.role)) return history.push('/v2/home')
  }, [auth])

  if (auth.status !== 'authorized') return null
  if (!['admin', 'superadmin'].includes(auth.user.role)) return null

  return <UsersContent />
}

// ─── Users Content ────────────────────────────────────
function UsersContent() {
  const auth = useWistorAuth()
  const mobile = useIsMobile()
  const usersQuery = useUsers()
  const projectsQuery = useProjects()
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()
  const deleteUser = useDeleteUser()

  const isSuperadmin = auth.status === 'authorized' && auth.user.role === 'superadmin'

  // State
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Dialogs
  const [createOpen, setCreateOpen] = useState(false)
  const [createValues, setCreateValues] = useState({ name: '', email: '', password: '', role: '', project: '' })
  const [editDialog, setEditDialog] = useState<UserItem | null>(null)
  const [editValues, setEditValues] = useState({ name: '', role: '', project: '' })
  const [passwordDialog, setPasswordDialog] = useState<{ id: string; name: string } | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [deleteDialog, setDeleteDialog] = useState<{ ids: string[]; names: string[] } | null>(null)
  const [bulkRoleDialog, setBulkRoleDialog] = useState(false)
  const [bulkRole, setBulkRole] = useState('')

  // Derived
  const users = useMemo(() => {
    if (!usersQuery.data) return []
    let items = orderBy(usersQuery.data, ['name'], ['asc']) as UserItem[]
    items = items.filter(u => !u.deleted)
    if (roleFilter !== 'all') items = items.filter(u => u.role === roleFilter)
    if (search.trim()) {
      const q = search.toLowerCase()
      items = items.filter(u =>
        u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.role?.toLowerCase().includes(q)
      )
    }
    return items
  }, [usersQuery.data, search, roleFilter])

  const projects = projectsQuery.data ?? []
  const allSelected = users.length > 0 && users.every(u => selected.has(u.id))
  const someSelected = selected.size > 0

  const toggleSelect = (id: string) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }
  const toggleAll = () => allSelected ? setSelected(new Set()) : setSelected(new Set(users.map(u => u.id)))

  // Handlers
  const handleCreate = () => {
    if (!createValues.name.trim() || !createValues.email.trim()) return
    createUser.mutate(createValues, {
      onSuccess: () => { setCreateOpen(false); setCreateValues({ name: '', email: '', password: '', role: '', project: '' }) },
      onError: () => alert('Failed to create user.'),
    })
  }

  const openEditDialog = (user: UserItem) => {
    setEditDialog(user)
    setEditValues({ name: user.name, role: user.role, project: user.project?.id ?? '' })
  }

  const handleEdit = () => {
    if (!editDialog) return
    const changes: { id: string } & Record<string, any> = { id: editDialog.id }
    if (editValues.name !== editDialog.name) changes.name = editValues.name
    if (editValues.role !== editDialog.role) changes.role = editValues.role
    if (isSuperadmin && editValues.project !== (editDialog.project?.id ?? '')) changes.project = editValues.project || null
    updateUser.mutate(changes, {
      onSuccess: () => setEditDialog(null),
      onError: () => alert('Failed to update user.'),
    })
  }

  const handlePasswordChange = () => {
    if (!passwordDialog || !newPassword.trim()) return
    updateUser.mutate(
      { id: passwordDialog.id, password: newPassword },
      { onSuccess: () => { setPasswordDialog(null); setNewPassword('') }, onError: () => alert('Failed to change password.') }
    )
  }

  const handleDelete = () => {
    if (!deleteDialog) return
    Promise.all(deleteDialog.ids.map(id => deleteUser.mutateAsync(id)))
      .then(() => { setDeleteDialog(null); setSelected(new Set()) })
      .catch(() => alert('Failed to delete some users.'))
  }

  const handleBulkRoleChange = () => {
    if (!bulkRole) return
    Promise.all(Array.from(selected).map(id => updateUser.mutateAsync({ id, role: bulkRole })))
      .then(() => { setBulkRoleDialog(false); setBulkRole(''); setSelected(new Set()) })
      .catch(() => alert('Failed to update some users.'))
  }

  if (usersQuery.isLoading) return <div className="v2-spinner" />

  return (
    <div className="v2-page">
      {/* Header */}
      <div className="v2-page-header">
        <div>
          <h1 className="v2-page-title">Users</h1>
          <p className="v2-page-subtitle">
            {users.length} user{users.length !== 1 ? 's' : ''}
            {roleFilter !== 'all' && ` (${roleFilter})`}
          </p>
        </div>
        <button className="v2-btn v2-btn--primary" onClick={() => setCreateOpen(true)}>
          <Icon d={ic.plus} size={14} />
          New User
        </button>
      </div>

      {/* Search + filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="v2-search-wrap">
          <span className="v2-search-icon"><Icon d={ic.search} size={15} /></span>
          <input
            className="v2-input"
            placeholder="Search by name, email, or role..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 38 }}
          />
        </div>
        <select
          className="v2-select"
          style={{ width: 'auto', maxWidth: 160 }}
          value={roleFilter}
          onChange={e => setRoleFilter(e.target.value)}
        >
          <option value="all">All roles</option>
          {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>

      {/* Bulk actions bar */}
      {someSelected && (
        <div className="v2-bulk-bar">
          <span className="v2-bulk-count">{selected.size} selected</span>
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button className="v2-btn v2-btn--secondary v2-btn--sm" onClick={() => setBulkRoleDialog(true)}>
              Change Role
            </button>
            <button
              className="v2-btn v2-btn--danger v2-btn--sm"
              onClick={() => {
                const targets = users.filter(u => selected.has(u.id))
                setDeleteDialog({ ids: targets.map(u => u.id), names: targets.map(u => u.name) })
              }}
            >
              Delete Selected
            </button>
            <button className="v2-btn v2-btn--ghost v2-btn--sm" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* ─── Mobile: cards ─── */}
      {mobile ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {users.map(user => (
            <div key={user.id} className={`v2-mobile-card ${selected.has(user.id) ? 'selected' : ''}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <input
                  type="checkbox"
                  className="v2-checkbox"
                  checked={selected.has(user.id)}
                  onChange={() => toggleSelect(user.id)}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--v2-text-primary)' }}>{user.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--v2-text-tertiary)' }}>{user.email}</div>
                </div>
                <span className={`v2-badge ${ROLE_BADGE_CLASS[user.role] || 'v2-badge--viewer'}`}>
                  {user.role}
                </span>
              </div>
              <div className="v2-mobile-card-footer">
                <span style={{ fontSize: 11, color: 'var(--v2-text-muted)' }}>
                  {user.project?.title ?? '—'}
                  {user.lastLogin ? ` · ${new Date(user.lastLogin).toLocaleDateString()}` : ''}
                </span>
                <div style={{ display: 'flex', gap: 2 }}>
                  <button className="v2-action v2-action--icon-only" onClick={() => openEditDialog(user)} title="Edit">
                    <Icon d={ic.edit} size={13} />
                  </button>
                  <button className="v2-action v2-action--icon-only" onClick={() => setPasswordDialog({ id: user.id, name: user.name })} title="Password">
                    <Icon d={ic.key} size={13} />
                  </button>
                  <button className="v2-action v2-action--icon-only v2-action--danger"
                          onClick={() => setDeleteDialog({ ids: [user.id], names: [user.name] })} title="Delete">
                    <Icon d={ic.trash} size={13} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* ─── Desktop: table ─── */
        <div className="v2-table-wrap">
          <div className="v2-table-scroll">
            <table className="v2-table">
              <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input type="checkbox" className="v2-checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                {isSuperadmin && <th>Project</th>}
                <th>Last Login</th>
                <th className="actions" style={{ textAlign: 'right' }}>Actions</th>
              </tr>
              </thead>
              <tbody>
              {users.map(user => (
                <tr key={user.id} className={selected.has(user.id) ? 'selected' : ''}>
                  <td style={{ width: 40 }}>
                    <input type="checkbox" className="v2-checkbox" checked={selected.has(user.id)} onChange={() => toggleSelect(user.id)} />
                  </td>
                  <td className="primary">{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                      <span className={`v2-badge ${ROLE_BADGE_CLASS[user.role] || 'v2-badge--viewer'}`}>
                        {user.role}
                      </span>
                  </td>
                  {isSuperadmin && <td>{user.project?.title ?? '—'}</td>}
                  <td style={{ fontSize: 12, color: 'var(--v2-text-tertiary)' }}>
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : '—'}
                  </td>
                  <td className="actions" style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <button
                        className="v2-action v2-action--icon-only"
                        onClick={() => openEditDialog(user)}
                        title="Edit"
                      >
                        <Icon d={ic.edit} size={14} />
                      </button>
                      <button
                        className="v2-action v2-action--icon-only"
                        onClick={() => setPasswordDialog({ id: user.id, name: user.name })}
                        title="Change Password"
                      >
                        <Icon d={ic.key} size={14} />
                      </button>
                      <button
                        className="v2-action v2-action--icon-only v2-action--danger"
                        onClick={() => setDeleteDialog({ ids: [user.id], names: [user.name] })}
                        title="Delete"
                      >
                        <Icon d={ic.trash} size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {users.length === 0 && (
        <div className="v2-empty">
          <div className="v2-empty-text">
            {search || roleFilter !== 'all' ? 'No users match your filters.' : 'No users found.'}
          </div>
        </div>
      )}

      {/* ─── Create User Dialog ─── */}
      <V2Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create User"
        footer={<>
          <button className="v2-btn v2-btn--ghost" onClick={() => setCreateOpen(false)}>Cancel</button>
          <button className="v2-btn v2-btn--primary" onClick={handleCreate} disabled={createUser.isPending}>
            {createUser.isPending ? 'Creating...' : 'Create'}
          </button>
        </>}
      >
        <div className="v2-field">
          <label className="v2-label">Name</label>
          <input className="v2-input" value={createValues.name}
                 onChange={e => setCreateValues(v => ({ ...v, name: e.target.value }))} autoFocus />
        </div>
        <div className="v2-field">
          <label className="v2-label">Email</label>
          <input className="v2-input" type="email" value={createValues.email}
                 onChange={e => setCreateValues(v => ({ ...v, email: e.target.value }))} />
        </div>
        <div className="v2-field">
          <label className="v2-label">Password</label>
          <input className="v2-input" type="password" value={createValues.password}
                 onChange={e => setCreateValues(v => ({ ...v, password: e.target.value }))} />
        </div>
        <div className="v2-field">
          <label className="v2-label">Role</label>
          <select className="v2-select" value={createValues.role}
                  onChange={e => setCreateValues(v => ({ ...v, role: e.target.value }))}>
            <option value="">Choose a role</option>
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
            <option value="admin">Administrator</option>
            {isSuperadmin && <option value="superadmin">SuperAdministrator</option>}
          </select>
        </div>
        {isSuperadmin && (
          <div className="v2-field">
            <label className="v2-label">Project</label>
            <select className="v2-select" value={createValues.project}
                    onChange={e => setCreateValues(v => ({ ...v, project: e.target.value }))}>
              <option value="">Choose a project</option>
              {projects.map((p: any) => (
                <option key={p.id || p._id} value={p.id || p._id}>{p.title}</option>
              ))}
            </select>
          </div>
        )}
      </V2Dialog>

      {/* ─── Edit User Dialog ─── */}
      <V2Dialog
        open={!!editDialog}
        onClose={() => setEditDialog(null)}
        title="Edit User"
        footer={<>
          <button className="v2-btn v2-btn--ghost" onClick={() => setEditDialog(null)}>Cancel</button>
          <button className="v2-btn v2-btn--primary" onClick={handleEdit} disabled={updateUser.isPending}>
            {updateUser.isPending ? 'Saving...' : 'Save'}
          </button>
        </>}
      >
        {editDialog && (
          <>
            <div className="v2-field">
              <label className="v2-label">Name</label>
              <input className="v2-input" value={editValues.name}
                     onChange={e => setEditValues(v => ({ ...v, name: e.target.value }))} autoFocus />
            </div>
            <div className="v2-field">
              <label className="v2-label">Email</label>
              <input className="v2-input" value={editDialog.email} disabled style={{ opacity: 0.6 }} />
              <p style={{ fontSize: 11, color: 'var(--v2-text-muted)', marginTop: 4 }}>Email cannot be changed here.</p>
            </div>
            <div className="v2-field">
              <label className="v2-label">Role</label>
              <select className="v2-select" value={editValues.role}
                      onChange={e => setEditValues(v => ({ ...v, role: e.target.value }))}>
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Administrator</option>
                {isSuperadmin && <option value="superadmin">SuperAdministrator</option>}
              </select>
            </div>
            {isSuperadmin && (
              <div className="v2-field">
                <label className="v2-label">Project</label>
                <select className="v2-select" value={editValues.project}
                        onChange={e => setEditValues(v => ({ ...v, project: e.target.value }))}>
                  <option value="">No project</option>
                  {projects.map((p: any) => (
                    <option key={p.id || p._id} value={p.id || p._id}>{p.title}</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </V2Dialog>

      {/* ─── Password Dialog ─── */}
      <V2Dialog
        open={!!passwordDialog}
        onClose={() => { setPasswordDialog(null); setNewPassword('') }}
        title="Change Password"
        footer={<>
          <button className="v2-btn v2-btn--ghost" onClick={() => { setPasswordDialog(null); setNewPassword('') }}>Cancel</button>
          <button className="v2-btn v2-btn--primary" onClick={handlePasswordChange} disabled={updateUser.isPending || !newPassword.trim()}>
            {updateUser.isPending ? 'Saving...' : 'Change Password'}
          </button>
        </>}
      >
        {passwordDialog && (
          <>
            <p style={{ fontSize: 14, color: 'var(--v2-text-secondary)', marginBottom: 16 }}>
              Set a new password for <strong>{passwordDialog.name}</strong>.
            </p>
            <div className="v2-field">
              <label className="v2-label">New Password</label>
              <input className="v2-input" type="password" value={newPassword}
                     onChange={e => setNewPassword(e.target.value)} autoFocus />
            </div>
          </>
        )}
      </V2Dialog>

      {/* ─── Delete Dialog ─── */}
      <V2Dialog
        open={!!deleteDialog}
        onClose={() => setDeleteDialog(null)}
        title={`Delete User${deleteDialog && deleteDialog.ids.length > 1 ? 's' : ''}`}
        footer={<>
          <button className="v2-btn v2-btn--ghost" onClick={() => setDeleteDialog(null)}>Cancel</button>
          <button className="v2-btn v2-btn--danger" onClick={handleDelete} disabled={deleteUser.isPending}>
            {deleteUser.isPending ? 'Deleting...' : 'Delete'}
          </button>
        </>}
      >
        {deleteDialog && (
          <div style={{ fontSize: 14, color: 'var(--v2-text-secondary)', lineHeight: 1.6 }}>
            <p>
              Are you sure you want to delete{' '}
              {deleteDialog.ids.length === 1
                ? <strong>"{deleteDialog.names[0]}"</strong>
                : <strong>{deleteDialog.ids.length} users</strong>
              }?
            </p>
            <p style={{ marginTop: 8, color: 'var(--v2-text-muted)' }}>
              This is a soft delete — accounts can be recovered later.
            </p>
          </div>
        )}
      </V2Dialog>

      {/* ─── Bulk Role Dialog ─── */}
      <V2Dialog
        open={bulkRoleDialog}
        onClose={() => { setBulkRoleDialog(false); setBulkRole('') }}
        title={`Change Role for ${selected.size} Users`}
        footer={<>
          <button className="v2-btn v2-btn--ghost" onClick={() => { setBulkRoleDialog(false); setBulkRole('') }}>Cancel</button>
          <button className="v2-btn v2-btn--primary" onClick={handleBulkRoleChange} disabled={!bulkRole || updateUser.isPending}>
            Apply
          </button>
        </>}
      >
        <div className="v2-field">
          <label className="v2-label">New Role</label>
          <select className="v2-select" value={bulkRole} onChange={e => setBulkRole(e.target.value)}>
            <option value="">Choose a role</option>
            <option value="viewer">Viewer</option>
            <option value="editor">Editor</option>
            <option value="admin">Administrator</option>
            {isSuperadmin && <option value="superadmin">SuperAdministrator</option>}
          </select>
        </div>
      </V2Dialog>
    </div>
  )
}